/**
 * Roles, capabilities and approval authority.
 *
 * Four roles, strictly ordered: owner > admin > member > viewer. A capability
 * check is "does this role sit at or above the minimum role for this action",
 * plus, for the two capabilities that carry real consequences, a per-person
 * flag: `can_approve` and `can_teach`.
 *
 * Two things this module exists to make structurally true:
 *
 *  - CONTRACT invariant 4: nothing in the agent's reachable surface can
 *    activate a skill or grant a tool. `AGENT_CAPABILITIES` is an allowlist of
 *    three read/propose actions, and `agentCan` consults nothing else.
 *  - Approval authority is bounded by money, not only by role. A member with
 *    `approval_limit_cents = 200_000` cannot approve a $5,000 payment however
 *    the request is phrased.
 *
 * Pure. No IO.
 */
import { ROLES } from './types.js';
const ROLE_RANK = { viewer: 0, member: 1, admin: 2, owner: 3 };
export function roleRank(role) {
    return ROLE_RANK[role];
}
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
/** True when `role` is at or above `minimum` in the hierarchy. */
export function roleAtLeast(role, minimum) {
    return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
/** The more privileged of two roles. */
export function maxRole(a, b) {
    return ROLE_RANK[a] >= ROLE_RANK[b] ? a : b;
}
/** The less privileged of two roles. */
export function minRole(a, b) {
    return ROLE_RANK[a] <= ROLE_RANK[b] ? a : b;
}
/* ------------------------------------------------------------------------- *
 * Capabilities
 * ------------------------------------------------------------------------- */
export const CAPABILITIES = [
    'overview.read',
    'agents.read',
    'agents.create',
    'agents.update',
    'agents.suspend',
    'chat.send',
    'runs.read',
    'runs.stop',
    'sessions.read',
    'approvals.read',
    'approvals.decide',
    'skills.read',
    'skills.propose',
    'skills.activate',
    'skills.decline',
    'skills.retire',
    'memories.read',
    'memories.write',
    'memories.pin',
    'memories.forget',
    'integrations.read',
    'integrations.connect',
    'integrations.disconnect',
    'integrations.test',
    'integrations.tools.toggle',
    'connectors.read',
    'people.read',
    'people.create',
    'people.update',
    'audit.read',
    'audit.export',
    'usage.read',
    'utilisation.read',
    'artifacts.read',
    'schedules.read',
    'schedules.update',
    'schedules.run',
    'triggers.read',
    'triggers.update',
    'policy.read',
    'policy.update',
    'settings.read',
    'settings.update',
    'tenant.update',
    'billing.read',
];
/** Minimum role for each capability. Absent from this table ⇒ nobody can do it. */
const MIN_ROLE = {
    'overview.read': 'viewer',
    'agents.read': 'viewer',
    'agents.create': 'owner',
    'agents.update': 'admin',
    'agents.suspend': 'admin',
    'chat.send': 'member',
    'runs.read': 'viewer',
    'runs.stop': 'member',
    'sessions.read': 'viewer',
    'approvals.read': 'viewer',
    'approvals.decide': 'member',
    'skills.read': 'viewer',
    'skills.propose': 'member',
    'skills.activate': 'admin',
    'skills.decline': 'admin',
    'skills.retire': 'admin',
    'memories.read': 'viewer',
    'memories.write': 'member',
    'memories.pin': 'member',
    'memories.forget': 'admin',
    'integrations.read': 'viewer',
    'integrations.connect': 'admin',
    'integrations.disconnect': 'admin',
    'integrations.test': 'admin',
    'integrations.tools.toggle': 'admin',
    'connectors.read': 'viewer',
    'people.read': 'viewer',
    'people.create': 'admin',
    'people.update': 'admin',
    'audit.read': 'admin',
    'audit.export': 'admin',
    'usage.read': 'member',
    'utilisation.read': 'admin',
    'artifacts.read': 'viewer',
    'schedules.read': 'viewer',
    'schedules.update': 'admin',
    'schedules.run': 'member',
    'triggers.read': 'admin',
    'triggers.update': 'admin',
    'policy.read': 'member',
    'policy.update': 'admin',
    'settings.read': 'viewer',
    'settings.update': 'admin',
    'tenant.update': 'owner',
    'billing.read': 'owner',
};
const CAPABILITY_SET = new Set(CAPABILITIES);
export function isCapability(value) {
    return typeof value === 'string' && CAPABILITY_SET.has(value);
}
/**
 * Role-level capability check. An unknown action is always `false` — a typo in
 * a route guard must fail closed, not open.
 */
export function can(role, action) {
    if (!isCapability(action))
        return false;
    return roleAtLeast(role, MIN_ROLE[action]);
}
/** The minimum role required for a capability, or `undefined` if unknown. */
export function minimumRoleFor(action) {
    return isCapability(action) ? MIN_ROLE[action] : undefined;
}
/** Every capability a role holds. Used to shape the console's nav and buttons. */
export function capabilitiesFor(role) {
    return CAPABILITIES.filter((capability) => roleAtLeast(role, MIN_ROLE[capability]));
}
/** Capabilities that additionally require the `can_approve` flag. */
export const APPROVE_FLAG_CAPABILITIES = ['approvals.decide'];
/** Capabilities that additionally require the `can_teach` flag. */
export const TEACH_FLAG_CAPABILITIES = [
    'skills.propose',
    'skills.activate',
    'skills.decline',
    'skills.retire',
    'memories.write',
    'memories.pin',
    'memories.forget',
];
export function isActivePerson(person) {
    return person.status === undefined || person.status === 'active';
}
/**
 * Capability check for an actual person: role hierarchy, account status, and
 * the two per-person flags.
 */
export function personCan(person, action) {
    if (!isActivePerson(person))
        return false;
    if (!can(person.role, action))
        return false;
    if (!isCapability(action))
        return false;
    if (APPROVE_FLAG_CAPABILITIES.includes(action) && person.canApprove !== true)
        return false;
    if (TEACH_FLAG_CAPABILITIES.includes(action) && person.canTeach !== true)
        return false;
    return true;
}
/** Negative limits mean "no cap". A limit of 0 means "may not approve money at all". */
export function isUnlimitedApprovalLimit(limitCents) {
    return limitCents === undefined || limitCents < 0;
}
/**
 * May this person decide this approval?
 *
 * Order matters: status, then flag, then role, then money. The first failure is
 * the one reported, because it is the one the client needs to fix.
 */
export function canApproveDecision(input) {
    const { person, requiredRole, amountCents } = input;
    if (!isActivePerson(person)) {
        return {
            allowed: false,
            code: 'not_active',
            reason: `This account is ${person.status ?? 'inactive'} and cannot decide approvals.`,
        };
    }
    if (person.canApprove !== true) {
        return {
            allowed: false,
            code: 'cannot_approve',
            reason: 'This person is not marked as an approver.',
        };
    }
    if (!can(person.role, 'approvals.decide')) {
        return {
            allowed: false,
            code: 'role_too_low',
            reason: `Role "${person.role}" cannot decide approvals.`,
        };
    }
    if (requiredRole !== undefined && !roleAtLeast(person.role, requiredRole)) {
        return {
            allowed: false,
            code: 'role_too_low',
            reason: `This approval requires the "${requiredRole}" role; this person is "${person.role}".`,
        };
    }
    if (input.allowSelfApproval === false &&
        input.requestedBy !== undefined &&
        person.id !== undefined &&
        input.requestedBy === person.id) {
        return {
            allowed: false,
            code: 'self_approval',
            reason: 'This person requested the action and may not approve it.',
        };
    }
    if (amountCents !== undefined && amountCents !== null && amountCents > 0) {
        const limit = person.approvalLimitCents;
        if (!isUnlimitedApprovalLimit(limit) && limit !== undefined && amountCents > limit) {
            return {
                allowed: false,
                code: 'over_limit',
                reason: `Amount exceeds this approver's limit (${String(amountCents)}c > ${String(limit)}c).`,
            };
        }
    }
    return { allowed: true, reason: 'Approver is authorised.' };
}
/** The smallest role that satisfies every approver in a list. Used for UI hints. */
export function highestRoleAmong(people) {
    let highest;
    for (const person of people) {
        highest = highest === undefined ? person.role : maxRole(highest, person.role);
    }
    return highest;
}
/* ------------------------------------------------------------------------- *
 * The agent principal — CONTRACT invariant 4
 * ------------------------------------------------------------------------- */
/**
 * Everything an agent may do through its own reachable surface (the in-process
 * `iq.*` MCP server). This is an allowlist, and it is the whole list.
 *
 * Note what is not here: `skills.activate`, `integrations.connect`,
 * `integrations.tools.toggle`, `policy.update`, `approvals.decide`. An agent
 * cannot activate a skill or grant itself a tool because there is no code path
 * that would let it — not because a prompt tells it not to.
 */
export const AGENT_CAPABILITIES = [
    'skills.read',
    'skills.propose', // `iq.save_skill` — writes `proposed` rows only
    'memories.read',
    'memories.write', // `iq.remember` — scoped memory writes
];
const AGENT_CAPABILITY_SET = new Set(AGENT_CAPABILITIES);
/** Capability check for the agent principal. Consults the allowlist and nothing else. */
export function agentCan(action) {
    return AGENT_CAPABILITY_SET.has(action);
}
/**
 * Is this capability safe while a tenancy or an agent is held by Otto?
 *
 * An Otto pause is "hold still and look": nothing runs and nothing changes, but
 * every run, approval, artifact and audit row stays readable. That distinction
 * is exactly the read/write split already encoded in the capability names, so
 * this reads it rather than maintaining a second list that would drift.
 *
 * `audit.export` is allowed despite not being a `.read`: it is how a client
 * reads an audit log too large to page through, and a pause that blocked it
 * would be withholding the very history the pause is meant to leave visible.
 */
export function isViewCapability(capability) {
    return capability.endsWith('.read') || capability === 'audit.export';
}
/** The capabilities an Otto pause leaves available. */
export const VIEW_CAPABILITIES = CAPABILITIES.filter(isViewCapability);
//# sourceMappingURL=rbac.js.map